import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Col, Empty, Row, Spin } from 'antd';
import { MainInfo } from './details/MainInfo';
import { Timetable } from './details/Timetable';
import { Configuration } from './details/Configuration';
import { NewConfiguration } from './details/NewConfiguration';
import { ExamsPanel } from './details/ExamsPanel';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplateDraftDetails, fetchTemplateDraftDetailsSuccess, setEditedExam } from '../templates-actions';
import { getDetailsLoading, getEditedExam, getAddedExam } from '../templates-selector';
import styles from '../../grid/BaseGrid.module.scss';
import { NewExamination } from './details/configurations/NewExamination';

export const DraftDetails = () => {
  const { templateId } = useParams<any>();
  const dispatch = useDispatch();
  const loading = useSelector(getDetailsLoading);
  const editedExam = useSelector(getEditedExam);
  const addedExam = useSelector(getAddedExam);

  // Shared state for new exam slot and panel visibility
  const [showNewExam, setShowNewExam] = useState(false);
  // Session-only set of modified slot ids (kept in memory only)
  const [sessionModifiedSlots, setSessionModifiedSlots] = useState(() => new Set<number>());
  // Session-only set of newly added slot ids
  const [sessionNewSlots, setSessionNewSlots] = useState(() => new Set<number>());
  // Ref for Timetable
  const timetableRef = useRef<any>(null);
  useEffect(() => {
    if (templateId) {
      dispatch(fetchTemplateDraftDetails({ id: templateId }));
    }
  }, [templateId]);

  useEffect(() => {
    return () => {
      dispatch(fetchTemplateDraftDetailsSuccess(null));
    };
  }, []);

  // Handler for Insert in NewExamination
  const handleInsertExam = (examDetails, fromEdit) => {
    // TODO: Update template details with new exam (API or Redux logic)
    setShowNewExam(false);
    if (examDetails && examDetails.examId && examDetails.examId !== 0) {
      if (fromEdit) {
        setSessionModifiedSlots((prev) => {
          const next = new Set(prev);
          next.add(examDetails.examId);
          return next;
        });
      } else {
        setSessionNewSlots((prev) => {
          const next = new Set(prev);
          next.add(examDetails.examId);
          return next;
        });
        try {
          timetableRef.current?.addSessionNewEvent(examDetails);
        } catch (e) {
          // ignore
        }
      }
    }
    if (timetableRef.current) timetableRef.current.removeNewSlot();
  };

  // This function will be called when the + button is clicked in ExamsPanel
  const handleAddExamClick = () => {
    setShowNewExam(true); // Show the NewExamination panel
  };

  // Handler to close NewExamination without insert
  const handleCloseNewExam = (isEdit) => {
    if (isEdit) dispatch(setEditedExam(null));
    else setShowNewExam(false);
    if (timetableRef.current) timetableRef.current.removeNewSlot();
  };

  const isCreateMode = false; //Only for edit mode
  // Only show configContent after clicking + icon (showNewExam)
  let showConfigContent = false;
  let configContent;
  if (editedExam) {
    configContent = (
      <NewConfiguration
        data={editedExam}
        onClosed={handleCloseNewExam}
        isEdit={true}
        onInserted={(exam, fromEdit) => handleInsertExam(exam, fromEdit)}
      />
    );
    showConfigContent = true;
  } else if (showNewExam && addedExam) {
    configContent = (
      <NewConfiguration
        data={addedExam}
        onClosed={handleCloseNewExam}
        onInserted={(exam, fromEdit) => handleInsertExam(exam, fromEdit)}
      />
    );
    showConfigContent = true;
  } else {
    configContent = <EmptyConfiguration description={<span>NO CONFIGURATION SELECTED</span>} />;
    showConfigContent = false;
  }

  return (
    <>
      <Spin spinning={loading}>
        <Row>
          <StyledLeftCol span={12}>
            <MainInfo
              isCreate={isCreateMode}
              isDraft={true}
              onUpdateSuccess={() => {
                setSessionModifiedSlots(new Set());
                setSessionNewSlots(new Set());
              }}
            />
            <ExamsPanel onAddExam={handleAddExamClick} isCreate={isCreateMode} />
            {/* Show configContent only after clicking + icon */}
            {showConfigContent && configContent}
          </StyledLeftCol>
          <StyledRightCol span={12}>
            <Timetable
              ref={timetableRef}
              isCreate={isCreateMode}
              sessionModifiedSlots={Array.from(sessionModifiedSlots)}
              sessionNewSlots={Array.from(sessionNewSlots)}
            />
          </StyledRightCol>
        </Row>
      </Spin>
      <StyledLogoWrapper>
        <img alt='logo' className={styles['logo']} src={'./images/logo.svg'} />
      </StyledLogoWrapper>
    </>
  );
};

const StyledLeftCol = styled(Col)`
  min-width: 300px;
  padding: 10px 20px 10px;
`;

const StyledRightCol = styled(Col)`
  min-width: 150px;
  padding: 10px 20px 10px;
`;

const StyledLogoWrapper = styled.div`
  width: 100%;
  padding: 40px 40px 20px;
  display: flex;
  justify-content: flex-end;
`;

const EmptyConfiguration = styled(Empty)`
  margin-top: 100px;

  span {
    opacity: 0.6;
  }
`;
